import React, { useState, useEffect } from "react";
import {
  Award,
  TrendingUp,
  Users,
  Target,
  BookOpen,
  RefreshCw,
  Download,
  Search,
  Filter,
  ArrowLeft,
  Eye,
  CheckCircle,
  XCircle,
  Loader,
  BarChart3,
  Star,
} from "lucide-react";
import { apiService } from "../../services/api";
import "./TopAssessments.css";

const TopAssessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSkill, setSelectedSkill] = useState("all");
  const [selectedDifficulty, setSelectedDifficulty] = useState("all");
  const [sortBy, setSortBy] = useState("completions");
  const [skills, setSkills] = useState([]);
  const [stats, setStats] = useState({
    totalAssessments: 0,
    totalCompletions: 0,
    averageScore: 0,
    topPerformer: null,
  });

  useEffect(() => {
    fetchAssessments();
    fetchSkills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    filterAndSortAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessments, searchTerm, selectedSkill, selectedDifficulty, sortBy]);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const response = await apiService.assessments.getTopAssessments(50);

      if (response && Array.isArray(response)) {
        setAssessments(response);
        calculateStats(response);
      } else {
        setAssessments([]);
      }
    } catch (error) {
      console.error("Error fetching assessments:", error);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkills = async () => {
    try {
      const response = await apiService.assessments.getSkills();
      if (response?.success && response.skills) {
        setSkills(response.skills);
      }
    } catch (error) {
      console.error("Error fetching skills:", error);
    }
  };

  const calculateStats = (data) => {
    const totalCompletions = data.reduce((sum, a) => sum + a.completions, 0);
    const avgScore =
      data.length > 0
        ? (
            data.reduce((sum, a) => sum + parseFloat(a.avgScore), 0) /
            data.length
          ).toFixed(1)
        : 0;
    const topPerformer =
      data.length > 0
        ? data.reduce(
            (max, a) => (a.completions > max.completions ? a : max),
            data[0],
          )
        : null;

    setStats({
      totalAssessments: data.length,
      totalCompletions,
      averageScore: avgScore,
      topPerformer,
    });
  };

  const filterAndSortAssessments = () => {
    let filtered = [...assessments];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((assessment) =>
        assessment.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Filter by skill
    if (selectedSkill !== "all") {
      filtered = filtered.filter(
        (assessment) => assessment.skillName === selectedSkill,
      );
    }

    // Filter by difficulty
    if (selectedDifficulty !== "all") {
      filtered = filtered.filter(
        (assessment) =>
          assessment.difficulty?.toLowerCase() ===
          selectedDifficulty.toLowerCase(),
      );
    }

    // Sort assessments
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "completions":
          return b.completions - a.completions;
        case "score":
          return parseFloat(b.avgScore) - parseFloat(a.avgScore);
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    setFilteredAssessments(filtered);
  };

  const handleExport = () => {
    const csvContent = [
      [
        "Rank",
        "Assessment Name",
        "Skill",
        "Difficulty",
        "Completions",
        "Average Score",
      ],
      ...filteredAssessments.map((assessment, index) => [
        index + 1,
        assessment.name,
        assessment.skillName || "N/A",
        assessment.difficulty || "N/A",
        assessment.completions,
        `${assessment.avgScore}%`,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `top-assessments-${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getScoreColor = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 80) return "#10b981";
    if (numScore >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner":
        return "#10b981";
      case "intermediate":
        return "#f59e0b";
      case "advanced":
        return "#ef4444";
      default:
        return "#6b7280";
    }
  };

  if (loading) {
    return (
      <div className="top-assessments loading">
        <div className="loading-container">
          <Loader size={48} className="spinner" />
          <h2>Loading Assessments...</h2>
          <p>Fetching assessment statistics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="top-assessments">
      {/* Header */}
      <div className="assessments-header">
        <div className="header-content">
          <button
            className="back-button"
            onClick={() => (window.location.href = "/admin/dashboard")}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1>
              <Award size={28} />
              Top Assessments
            </h1>
            <p>Comprehensive assessment performance and statistics</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={fetchAssessments}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={handleExport}>
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="assessments-stats">
        <div className="stat-card blue">
          <div className="stat-icon">
            <BookOpen size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalAssessments}</div>
            <div className="stat-label">Total Assessments</div>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">
              {stats.totalCompletions.toLocaleString()}
            </div>
            <div className="stat-label">Total Completions</div>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <Target size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.averageScore}%</div>
            <div className="stat-label">Average Score</div>
          </div>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">
            <Star size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">
              {stats.topPerformer
                ? stats.topPerformer.name.substring(0, 15) + "..."
                : "N/A"}
            </div>
            <div className="stat-label">Top Performer</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="assessments-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search assessments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <XCircle size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select
            value={selectedSkill}
            onChange={(e) => setSelectedSkill(e.target.value)}
          >
            <option value="all">All Skills</option>
            {skills.map((skill) => (
              <option key={skill.skill_id} value={skill.skill_name}>
                {skill.skill_name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <Target size={18} />
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
          >
            <option value="all">All Difficulties</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div className="filter-group">
          <BarChart3 size={18} />
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="completions">Most Completions</option>
            <option value="score">Highest Score</option>
            <option value="name">Name (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Assessments List */}
      <div className="assessments-content">
        {filteredAssessments.length === 0 ? (
          <div className="empty-state">
            <Eye size={64} />
            <h3>No Assessments Found</h3>
            <p>
              {searchTerm ||
              selectedSkill !== "all" ||
              selectedDifficulty !== "all"
                ? "Try adjusting your filters"
                : "No assessments available yet"}
            </p>
          </div>
        ) : (
          <div className="assessments-grid">
            {filteredAssessments.map((assessment, index) => (
              <div key={assessment.id || index} className="assessment-card">
                <div className="card-header">
                  <div className="rank-badge">#{index + 1}</div>
                  <button
                    className="view-details-btn"
                    onClick={() =>
                      (window.location.href = `/admin/assessments/${assessment.id}`)
                    }
                  >
                    <Eye size={16} />
                  </button>
                </div>

                <div className="card-body">
                  <h3 className="assessment-title">{assessment.name}</h3>

                  <div className="assessment-tags">
                    {assessment.skillName && (
                      <span className="skill-tag">
                        <BookOpen size={14} />
                        {assessment.skillName}
                      </span>
                    )}
                    {assessment.difficulty && (
                      <span
                        className="difficulty-tag"
                        style={{
                          backgroundColor: `${getDifficultyColor(assessment.difficulty)}15`,
                          color: getDifficultyColor(assessment.difficulty),
                        }}
                      >
                        {assessment.difficulty}
                      </span>
                    )}
                  </div>

                  <div className="assessment-metrics">
                    <div className="metric-row">
                      <div className="metric-item">
                        <Users size={16} />
                        <span className="metric-label">Completions</span>
                        <span className="metric-value">
                          {assessment.completions.toLocaleString()}
                        </span>
                      </div>
                      <div className="metric-item">
                        <Target size={16} />
                        <span className="metric-label">Avg Score</span>
                        <span
                          className="metric-value"
                          style={{ color: getScoreColor(assessment.avgScore) }}
                        >
                          {assessment.avgScore}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="score-visualization">
                    <div className="score-bar">
                      <div
                        className="score-fill"
                        style={{
                          width: `${assessment.avgScore}%`,
                          backgroundColor: getScoreColor(assessment.avgScore),
                        }}
                      ></div>
                    </div>
                    <div className="score-labels">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  {assessment.completions > 0 && (
                    <div className="popularity-indicator">
                      <TrendingUp size={14} />
                      <span>
                        {assessment.completions > 100
                          ? "Very Popular"
                          : assessment.completions > 50
                            ? "Popular"
                            : "Growing"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopAssessments;
